import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionDetailsDto,
    OrganisationAuthorityService,
    PersonRolesDto,
    PersonService,
    PersonWithRoles,
    RegisterEntrySummary,
    WorkTracker,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import {
    BehaviorSubject,
    Subject,
    catchError,
    concatMap,
    debounceTime,
    forkJoin,
    map,
    of,
    takeUntil
} from 'rxjs';
import { FormatMissionEditRegisterStatus } from '../formatMissionEditRegisterStatus.pipe';
import { PersonWithRegisterStatus } from '../mission-edit.service';

@Injectable()
export class MissionRpicSelectDataService {
    organisationId: number;
    rpicId: number;
    excludedIds: number[];
    workgroupPersonnelIds: number[];
    allWorkgroupPersonnelLoaded = false;
    roleFilter: PersonRolesDto.Roles;
    missionDetails: MissionDetailsDto;

    personSearch = personSearch.bind(this);

    private personnelSource = new BehaviorSubject<PersonWithRegisterStatus[]>(
        []
    );
    personnel$ = this.personnelSource.asObservable();

    private personSearchSource = new Subject<NgSelectSearch>();
    personSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    private authorityIdsSource = new BehaviorSubject<number[]>(null);
    authorityIds$ = this.authorityIdsSource.asObservable();

    private workTracker = new WorkTracker();
    private workingSource = new BehaviorSubject<boolean>(false);
    working$ = this.workingSource.asObservable();
    private ngUnsubscribe$ = new Subject<void>();
    private refreshData$ = new Subject<void>();

    constructor(
        private personService: PersonService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.personSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personSelectConfig
                );
                this.refreshData();
            });
    }

    ngOnDestroy() {
        this.personSearchSource.complete();
        this.personnelSource.complete();
        this.authorityIdsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.refreshData$.next();
        this.refreshData$.complete();
    }

    /**
     * setting the parameters kicks off the service properly. Each time parameters are updated the fetched items will be reset.
     * @param organisationId The current organisation ID (Managing organisation ID will be derived from the mission).
     * @param role A role to use for filtering results.
     * @param excludeIds Personnel to exclude from the search.
     * @param missionDetails The current mission as a MissionDetailsDto.
     * @param workgroupPersonnelIds Personnel in the currently selected workgroup. This will be used to fetch those personnel first and group them in the ng-select.
     * @param rpicId The current RPIC. This is used to prefetch the RPIC in case they aren't on the first page of results from the personnel list.
     */
    setParameters(
        organisationId: number,
        role: PersonRolesDto.Roles,
        excludeIds: number[],
        missionDetails: MissionDetailsDto,
        workgroupPersonnelIds: number[],
        rpicId: number
    ) {
        // Stop all current fetches
        this.refreshData$.next();
        this.organisationId = organisationId;
        this.roleFilter = role;
        this.excludedIds = excludeIds;
        this.missionDetails = missionDetails;
        this.workgroupPersonnelIds = workgroupPersonnelIds;
        this.rpicId = rpicId;
        this.findMissionAuthorities();
        this.resetPersonSelectConfig();
        this.refreshData();
    }

    private findMissionAuthorities() {
        if (this.missionDetails == null) {
            this.authorityIdsSource.next(null);
        } else {
            const workflowAuthorities =
                this.missionDetails.missionWorkflowVersion
                    ?.delegatedAuthority != null
                    ? [
                          this.missionDetails.missionWorkflowVersion
                              .delegatedAuthorityId
                      ].concat(
                          this.missionDetails.missionWorkflowVersion
                              .additionalAuthorityIds ?? []
                      )
                    : this.missionDetails.missionWorkflowVersion
                          ?.additionalAuthorities != null
                    ? this.missionDetails.missionWorkflowVersion
                          .additionalAuthorityIds
                    : [];

            const additionalAuthorities =
                this.missionDetails.additionalAuthorities?.map(a => a.id) ?? [];

            this.authorityIdsSource.next(
                workflowAuthorities.concat(additionalAuthorities)
            );
        }
    }

    /**
     * Each time the data refreshes, the flow is:
     * - Check if the RPIC has already been fetched. If not, fetch the RPIC first (See the documentation on prefetching in libraries.md)
     * - If there's a workgroup selected, fetch the personnel from the workgroup first
     * - Fetch the remaining personnel by excluding the IDs of people already fetched.
     */
    private refreshData() {
        const hasPilotInData =
            this.personnel.length > 0 &&
            this.personnel.find(person => person.id === this.rpicId) != null;
        if (!hasPilotInData && this.rpicId != null) {
            return this.prefetchRpic();
        }
        if (
            this.workgroupPersonnelIds == null ||
            this.workgroupPersonnelIds.length === 0 ||
            this.allWorkgroupPersonnelLoaded
        ) {
            this.refreshPersonList();
        } else {
            this.refreshWorkgroupPersonnel();
        }
    }

    private prefetchRpic() {
        this.personService
            .findPersonnelGraphQL(this.organisationId, 0, 1, {
                idList: [this.rpicId]
            })
            .pipe(
                concatMap(result => {
                    return this.findAuthorityRegisterStatuses(
                        this.buildPersonWithRegisterStatus(result.results)
                    );
                }),
                takeUntil(this.refreshData$)
            )
            .subscribe({
                next: (newPersonnel: PersonWithRegisterStatus[]) => {
                    const updatedPersonnel = this.updateWorkgroupStatus(
                        this.personnel.concat(newPersonnel)
                    );

                    this.personnelSource.next(updatedPersonnel);
                    this.refreshData();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading mission personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    private refreshWorkgroupPersonnel() {
        let count = 0;
        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.personSelectConfig.currentPage,
                15,
                {
                    role: this.roleFilter,
                    textSearch:
                        this.personSelectConfig.searchValue || undefined,
                    excludedIdList: this.excludedIds,
                    idList: this.workgroupPersonnelIds
                }
            )
            .pipe(
                concatMap(result => {
                    count = result.count;
                    const newPersonnel = result.results.filter(
                        person =>
                            this.personnelSource
                                .getValue()
                                .find(p => p.id === person.id) == null
                    );

                    this.personSelectConfig = updateNgSelectData(
                        result,
                        this.personSelectConfig
                    );

                    return this.findAuthorityRegisterStatuses(
                        this.buildPersonWithRegisterStatus(newPersonnel)
                    );
                }),
                takeUntil(this.refreshData$)
            )
            .subscribe({
                next: (newPersonnel: PersonWithRegisterStatus[]) => {
                    const updatedPersonnel = this.updateWorkgroupStatus(
                        this.personnel.concat(newPersonnel)
                    );

                    this.personnelSource.next(updatedPersonnel);
                    if (this.personnel.length === count) {
                        this.allWorkgroupPersonnelLoaded = true;
                        this.personSelectConfig = {
                            ...this.personSelectConfig,
                            currentPage: 0,
                            totalItems: null
                        };
                        this.refreshPersonList();
                    }
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading mission personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    private refreshPersonList() {
        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.personSelectConfig.currentPage,
                15,
                {
                    role: this.roleFilter,
                    textSearch:
                        this.personSelectConfig.searchValue || undefined,
                    excludedIdList: (this.excludedIds ?? []).concat(
                        this.workgroupPersonnelIds ?? []
                    )
                }
            )
            .pipe(
                concatMap(result => {
                    const newPersonnel = result.results.filter(
                        person =>
                            this.personnelSource
                                .getValue()
                                .find(p => p.id === person.id) == null
                    );
                    this.personSelectConfig = updateNgSelectData(
                        result,
                        this.personSelectConfig
                    );

                    return this.findAuthorityRegisterStatuses(
                        this.buildPersonWithRegisterStatus(newPersonnel)
                    );
                }),
                takeUntil(this.refreshData$)
            )
            .subscribe({
                next: (newPersonnel: PersonWithRegisterStatus[]) => {
                    const updatedPersonnel = this.updateWorkgroupStatus(
                        this.personnel.concat(newPersonnel)
                    );

                    this.personnelSource.next(updatedPersonnel);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading mission personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    /**
     * A helper function to parse down the results from the personnel list query to allow adding register status details
     * @param results The PersonWithRoles result list from the GraphQL Personnel list query
     * @returns A PersonWithRegisterStatus list, but with null register statuses for everyone. Register statuses will be added in a subsequent fetch.
     */
    private buildPersonWithRegisterStatus(results: PersonWithRoles[]) {
        return (
            results
                ?.filter(
                    p =>
                        p != null &&
                        this.personnel.findIndex(
                            person => person.id === p.id
                        ) === -1
                )
                .map<PersonWithRegisterStatus>(
                    p =>
                        ({
                            id: p.id,
                            firstName: p.firstName,
                            lastName: p.lastName,
                            email: p.email,
                            phoneNumber: p.phoneNumber,
                            roles: p.roles,
                            workgroups: p.workgroupList,
                            active: p.active,
                            registerStatuses: null
                        } as any)
                )
                .filter(p => p != null && p.id != null) ?? []
        );
    }

    /**
     * A helper function that attaches a status string to indicate whether a given person is in the current workgroup or not.
     * @param personnel The personnel to assign workgroup statuses
     * @returns A PersonWithRegisterStatus object for each person that includes their registerWithWorkgroupStatus if a workgroup has been selected.
     */
    private updateWorkgroupStatus(personnel: PersonWithRegisterStatus[]) {
        const formatStatus = new FormatMissionEditRegisterStatus();
        if (this.workgroupPersonnelIds == null) {
            return personnel;
        }
        const withWorkgroupStatus = personnel.map(person => ({
            ...person,
            registerWithWorkgroupStatus:
                this.workgroupPersonnelIds != null &&
                this.workgroupPersonnelIds.includes(person.id)
                    ? `In Workgroup`
                    : `Not In Workgroup`
        }));
        // FIXME: When having the grouping include register status it begins to break down as people not in a workgroup don't necessarily get inserted in the bottom of the list.

        /* const withWorkflowStatus = updatedPersonnel.map(person => ({
            ...person,
            registerWithWorkgroupStatus: `${
                this.workgroupPersonnelIds != null &&
                this.workgroupPersonnelIds.includes(person.id)
                    ? 'In Workgroup - '
                    : this.workgroupPersonnelIds != null &&
                      !this.workgroupPersonnelIds.includes(person.id)
                    ? 'Not In Workgroup - '
                    : ''
            }${formatStatus.transform(
                person.registerStatuses != null &&
                    this.missionDetails?.missionWorkflowVersion
                        ?.delegatedAuthorityId != null
                    ? (person.registerStatuses[
                          this.missionDetails?.missionWorkflowVersion
                              ?.delegatedAuthorityId
                      ] as PilotRegisterStatusUnion)
                    : (null as PilotRegisterStatusUnion)
            )}`
        })); */

        return withWorkgroupStatus;
    }

    /**
     * Takes the list of personnel from the last fetch and finds the register statuses for each.
     * @returns the same list of personnel, but with their registerStatuses fields updated if the mission has authorities attached to it.
     */
    private findAuthorityRegisterStatuses(
        newPersonnel: PersonWithRegisterStatus[]
    ) {
        const authorityIds = this.authorityIdsSource.getValue();
        if (authorityIds?.length > 0 && newPersonnel.length > 0) {
            const personnelIds = newPersonnel.map(p => p.id);
            const time =
                this.missionDetails?.missionDate ?? toTimestamp(new Date());
            return forkJoin(
                authorityIds.map(authId =>
                    this.organisationAuthorityService
                        .findPersonnelRegisterStatuses(
                            authId,
                            personnelIds,
                            time,
                            this.missionDetails?.organisationId ??
                                this.organisationId
                        )
                        .pipe(
                            map(results => ({
                                [authId]: results.reduce(
                                    (acc, result) => ({
                                        ...acc,
                                        [result.entityId]: result.registerStatus
                                    }),
                                    {}
                                )
                            })),
                            catchError((error: FlyFreelyError) => {
                                this.logging.error(
                                    error,
                                    `Error while updating register statuses for personnel: ${error.message}`
                                );
                                return of({
                                    [authId]: personnelIds.reduce(
                                        (acc, id) => ({
                                            ...acc,
                                            [id]: RegisterEntrySummary
                                                .RegisterStatus.NOT_AVAILABLE
                                        }),
                                        {}
                                    )
                                });
                            }),
                            takeUntil(this.ngUnsubscribe$)
                        )
                )
            ).pipe(
                map(results => {
                    const combinedResults = results.reduce((acc, authority) => {
                        const ids = Object.keys(authority);
                        return ids.reduce(
                            (acc2, id) => ({
                                ...acc2,
                                [id]: {
                                    ...acc2[id],
                                    ...authority[id]
                                }
                            }),
                            {}
                        );
                    }, {});
                    const authorityIds =
                        combinedResults != null
                            ? Object.keys(combinedResults)
                            : [];
                    return newPersonnel.map(p => ({
                        ...p,
                        registerStatuses: authorityIds.reduce(
                            (acc, id) => ({
                                ...acc,
                                [id]:
                                    combinedResults[id] != null
                                        ? combinedResults[id][p.id]
                                        : null
                            }),
                            {}
                        )
                    }));
                })
            );
        } else {
            return of(newPersonnel);
        }
    }

    onPersonSelectScroll() {
        const resultLength =
            this.personSelectConfig.searchValue == null
                ? this.personnel.length
                : this.personnel.filter(person =>
                      this.personSearch(
                          this.personSelectConfig.searchValue,
                          person
                      )
                  ).length;

        if (
            this.personSelectConfig.totalItems == null ||
            resultLength !== this.personSelectConfig.totalItems
        ) {
            this.refreshData();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.resetPersonSelectConfig();
        this.refreshData();
    }

    private resetPersonSelectConfig() {
        this.personnelSource.next([]);
        this.workgroupPersonnelIds = [];
        this.allWorkgroupPersonnelLoaded = false;
        this.personSelectConfig = {
            ...this.personSelectConfig,
            searchValue: null,
            currentPage: 0,
            preSearchPage: null
        };
    }

    private get personnel() {
        return this.personnelSource.getValue();
    }
}
